import { useState, useCallback, useMemo, useRef } from 'react'
import { Head, Link } from '@inertiajs/react'
import clsx from 'clsx'
import AppLayout from '@/layouts/AppLayout'
import PageHeader from '@/components/ui/PageHeader'
import Notification from '@/components/ui/Notification'
import DataTable, { useFlash, usePagination, type DataTableColumn } from '@/components/ui/DataTable'
import { FormSelect, FormInput, FormTextarea, FormMultiSelect } from '@/components/ui/form'
import Button from '@/components/ui/Button'
import Pagination from '@/components/ui/Pagination'
import { Badge } from '@/components/catalyst/badge'
import { Dialog, DialogTitle, DialogBody, DialogActions } from '@/components/catalyst/dialog'
import { TrashIcon, EnvelopeIcon, CheckCircleIcon, PlusIcon, TableCellsIcon, CalendarDaysIcon, ChevronLeftIcon, ChevronRightIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { ChevronDownIcon } from '@heroicons/react/20/solid'
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react'
import Avatar from '@/components/ui/Avatar'
import Drawer from '@/components/ui/Drawer'

// ─── Types ──────────────────────────────────────

interface TaskRow {
  id: number
  title: string
  description: string | null
  start_date: string | null
  deadline_date: string | null
  completed: boolean
  completion_date: string | null
  assignee_id: number | null
  assignee_name: string | null
  user_id: number
  user_name: string | null
  order_id: number | null
  order_po_number: string | null
  created_at: string | null
}

interface TasksIndexProps {
  tasks: TaskRow[]
  task_titles: string[]
  users: { id: number; name: string }[]
  orders: { id: number; po_number: string }[]
  current_user_id: number
  selectedOrder?: string
  tasksFilteredByOrder?: string
}

// ─── Helpers ────────────────────────────────────

function formatDate(d: string | null): string {
  if (!d) return '—'
  const [y, m, day] = d.split('-')
  return `${m}/${day}/${y}`
}

function isOverdue(d: string | null, completed: boolean): boolean {
  if (!d || completed) return false
  return new Date(d) < new Date(new Date().toISOString().slice(0, 10))
}

function isDueSoon(d: string | null, completed: boolean): boolean {
  if (!d || completed) return false
  const deadline = new Date(d)
  const now = new Date(new Date().toISOString().slice(0, 10))
  const diff = (deadline.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
  return diff >= 0 && diff <= 3
}

const PAGE_SIZES = [30, 50, 100, 200]

// ─── Component ──────────────────────────────────

export default function TasksIndex({ tasks: initialTasks, task_titles, users, orders, current_user_id, selectedOrder, tasksFilteredByOrder }: TasksIndexProps) {
  const [tasks, setTasks] = useState(() => {
    if (tasksFilteredByOrder) return initialTasks.filter(t => String(t.order_id) === String(tasksFilteredByOrder))
    return initialTasks
  })
  const [filterByOrder] = useState(tasksFilteredByOrder || '')
  const [newTaskOpen, setNewTaskOpen] = useState(false)
  const [notifMessage, setNotifMessage] = useState('')
  const [showNotif, setShowNotif] = useState(false)
  const { flashRow, flash } = useFlash()

  const csrfToken = typeof document !== 'undefined'
    ? document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
    : ''

  const notify = useCallback((msg: string) => { setNotifMessage(msg); setShowNotif(true) }, [])
  const [viewMode, setViewMode] = useState<'table' | 'calendar'>('calendar')
  const [calMonth, setCalMonth] = useState(() => { const d = new Date(); return { year: d.getFullYear(), month: d.getMonth() } })
  const [calScale, setCalScale] = useState<'month' | 'week' | 'year'>('month')
  const [weekStart, setWeekStart] = useState<string>(() => {
    const d = new Date()
    d.setDate(d.getDate() - d.getDay()) // Start of current week (Sunday)
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  })
  const [drawerDate, setDrawerDate] = useState<string | null>(null)
  const [drawerTasks, setDrawerTasks] = useState<TaskRow[]>([])

  const openDrawer = useCallback((dateLabel: string, tasksForDate: TaskRow[]) => {
    setDrawerDate(dateLabel)
    setDrawerTasks(tasksForDate)
  }, [])
  const closeDrawer = useCallback(() => { setDrawerDate(null); setDrawerTasks([]) }, [])

  // ─── Options ────────────────────────────────
  const pageSizeOptions = PAGE_SIZES.map(n => ({ value: String(n), label: `${n} per page` }))

  // ─── Pagination ─────────────────────────────
  const { pageSize, setPageSize, currentPage, setCurrentPage, totalPages, paginatedRows, totalCount } = usePagination(tasks)

  // ─── Toggle complete ────────────────────────
  const toggleComplete = useCallback(async (task: TaskRow) => {
    try {
      const res = await fetch(`/tasks/${task.id}/complete.json`, {
        method: 'POST',
        headers: { 'X-CSRF-Token': csrfToken },
      })
      if (res.ok) {
        const updated = await res.json()
        setTasks(prev => prev.map(t => t.id === task.id ? { ...t, completed: updated.completed, completion_date: updated.completion_date } : t))
        flash(task.id, 'success')
        notify(updated.completed ? 'Saved:Task completed' : 'Saved:Task reopened')
      } else { flash(task.id, 'error') }
    } catch { flash(task.id, 'error') }
  }, [csrfToken, flash, notify])

  // ─── Delete ─────────────────────────────────
  const deleteTask = useCallback(async (id: number) => {
    if (!confirm('Are you sure you want to delete this task?')) return
    try {
      const res = await fetch(`/tasks/${id}/delete.json`, { method: 'POST', headers: { 'X-CSRF-Token': csrfToken } })
      if (res.ok) { setTasks(prev => prev.filter(t => t.id !== id)); notify('Deleted:Task removed') }
    } catch { /* silent */ }
  }, [csrfToken, notify])

  // ─── Email reminder ─────────────────────────
  const sendReminder = useCallback(async (id: number) => {
    try {
      const res = await fetch(`/tasks/${id}/email_reminder.json`, { method: 'POST', headers: { 'X-CSRF-Token': csrfToken } })
      if (res.ok) { flash(id, 'success'); notify('Saved:Reminder sent') }
      else { flash(id, 'error') }
    } catch { flash(id, 'error') }
  }, [csrfToken, flash, notify])

  // ─── Create task ────────────────────────────
  const handleCreate = useCallback(async (data: Record<string, string>) => {
    try {
      const res = await fetch('/tasks.json', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
        body: JSON.stringify({ task: data }),
      })
      if (res.ok) {
        const task = await res.json()
        setTasks(prev => [task, ...prev])
        setNewTaskOpen(false)
        notify('Saved:Task created')
      } else {
        const err = await res.json().catch(() => ({}))
        notify(`error:Error:${err.errors?.join(', ') || 'Failed to create'}`)
      }
    } catch { notify('error:Error:Network error') }
  }, [csrfToken, notify])

  // ─── Columns ────────────────────────────────
  const columns: DataTableColumn<TaskRow>[] = [
    {
      key: 'title', label: 'Title',
      render: row => (
        <span className={clsx('font-medium', row.completed && 'line-through opacity-50')}>
          {row.title}
        </span>
      ),
      sortValue: row => row.title,
      filterValue: row => row.title,
    },
    {
      key: 'order_po_number', label: 'Order #',
      render: row => row.order_id
        ? <Link href={`/inertia_customer_orders/${row.order_id}`} className="text-indigo-600 hover:underline dark:text-indigo-400">{row.order_po_number || `#${row.order_id}`}</Link>
        : <span className="text-zinc-400">—</span>,
      sortValue: row => row.order_po_number || '',
      filterValue: row => row.order_po_number || '',
    },
    {
      key: 'assignee_name', label: 'Assignee',
      render: row => <>{row.assignee_name || '—'}</>,
      sortValue: row => row.assignee_name || '',
      filterValue: row => row.assignee_name || '',
    },
    {
      key: 'user_name', label: 'Assignor',
      render: row => <>{row.user_name || '—'}</>,
      sortValue: row => row.user_name || '',
      filterValue: row => row.user_name || '',
    },
    {
      key: 'start_date', label: 'Start',
      render: row => <>{formatDate(row.start_date)}</>,
      sortValue: row => row.start_date || '',
      filterValue: row => row.start_date || '',
    },
    {
      key: 'deadline_date', label: 'Deadline',
      render: row => {
        const overdue = isOverdue(row.deadline_date, row.completed)
        const soon = isDueSoon(row.deadline_date, row.completed)
        return (
          <span className={clsx(row.completed && 'opacity-50')}>
            {overdue ? <Badge color="red">{formatDate(row.deadline_date)}</Badge>
              : soon ? <Badge color="amber">{formatDate(row.deadline_date)}</Badge>
              : formatDate(row.deadline_date)}
          </span>
        )
      },
      sortValue: row => row.deadline_date || '',
      filterValue: row => row.deadline_date || '',
    },
    {
      key: 'completed', label: 'Status',
      render: row => row.completed
        ? <Badge color="emerald">Completed</Badge>
        : <Badge color="sky">Open</Badge>,
      sortValue: row => row.completed ? 1 : 0,
      filterValue: row => row.completed ? 'Completed' : 'Open',
    },
  ]

  // ─── Render ─────────────────────────────────
  return (
    <>
      <Head title="Tasks" />
      <Notification message={notifMessage} show={showNotif} onClose={() => setShowNotif(false)} />

      <PageHeader title="Tasks" icon={<CheckCircleIcon className="h-5 w-5" />} actions={
        <div className="flex items-center gap-2">
          {/* View toggle */}
          <div className="flex rounded-lg border border-zinc-200 dark:border-zinc-700">
            <button onClick={() => setViewMode('table')} className={clsx('rounded-l-lg px-2 py-1', viewMode === 'table' ? 'bg-indigo-500 text-white' : 'text-zinc-500 hover:text-zinc-700 dark:text-zinc-400')} title="Table view">
              <TableCellsIcon className="h-4 w-4" />
            </button>
            <button onClick={() => setViewMode('calendar')} className={clsx('rounded-r-lg px-2 py-1', viewMode === 'calendar' ? 'bg-indigo-500 text-white' : 'text-zinc-500 hover:text-zinc-700 dark:text-zinc-400')} title="Calendar view">
              <CalendarDaysIcon className="h-4 w-4" />
            </button>
          </div>
          {viewMode === 'table' && (
            <div className="w-36">
              <FormSelect value={String(pageSize)} onChange={v => { setPageSize(Number(v)); setCurrentPage(0) }} options={pageSizeOptions} />
            </div>
          )}
          {viewMode === 'table' && (
            <Button onClick={() => setNewTaskOpen(true)}>
              <PlusIcon className="mr-1 h-4 w-4" /> New Task
            </Button>
          )}
        </div>
      } />

      <div className="mb-2 flex items-center gap-2">
        <Badge color="zinc"><span className="text-xs font-medium">{totalCount} tasks</span></Badge>
        {filterByOrder && (
          <Badge color="indigo"><span className="text-xs font-medium">Filtered by Order #{filterByOrder}</span></Badge>
        )}
      </div>

      {viewMode === 'table' ? (
        <>
          <DataTable
            rows={paginatedRows}
            columns={columns}
            rowKey={row => row.id}
            flashRowId={flashRow?.id ?? null}
            flashStatus={flashRow?.status ?? null}
            emptyText="No tasks"
            filteredEmptyText="No tasks match your filters"
            actions={row => (
              <div className="flex items-center gap-1">
                {row.user_id !== current_user_id && (
                  <button onClick={() => toggleComplete(row)} className={clsx('rounded p-1', row.completed ? 'text-emerald-500 hover:text-emerald-700' : 'text-zinc-400 hover:text-emerald-500')} title={row.completed ? 'Reopen' : 'Complete'}>
                    <CheckCircleIcon className="h-4 w-4" />
                  </button>
                )}
                <button onClick={() => sendReminder(row.id)} className="rounded p-1 text-zinc-400 hover:text-indigo-600 dark:hover:text-indigo-400" title="Send reminder">
                  <EnvelopeIcon className="h-4 w-4" />
                </button>
                <button onClick={() => deleteTask(row.id)} className="rounded p-1 text-zinc-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-500/10 dark:hover:text-red-400" title="Delete">
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            )}
          />
          <Pagination currentPage={currentPage} totalPages={totalPages} pageSize={pageSize} totalCount={totalCount} onPageChange={setCurrentPage} />
        </>
      ) : (
        <TaskCalendar tasks={tasks} month={calMonth} onMonthChange={setCalMonth} scale={calScale} onScaleChange={setCalScale} weekStart={weekStart} onWeekStartChange={setWeekStart} currentUserId={current_user_id} onComplete={toggleComplete} onDelete={deleteTask} onReminder={sendReminder} onDayClick={openDrawer} onNewTask={() => setNewTaskOpen(true)} />
      )}

      {/* Tasks Drawer — slides in from right */}
      <TaskDrawer
        open={!!drawerDate}
        dateLabel={drawerDate || ''}
        tasks={drawerTasks}
        currentUserId={current_user_id}
        onComplete={toggleComplete}
        onDelete={deleteTask}
        onReminder={sendReminder}
        onClose={closeDrawer}
      />

      {/* New Task Dialog */}
      <NewTaskDialog
        open={newTaskOpen}
        onClose={() => setNewTaskOpen(false)}
        onSubmit={handleCreate}
        taskTitles={task_titles}
        users={users}
        orders={orders}
        selectedOrder={selectedOrder}
      />
    </>
  )
}

// ─── New Task Dialog ────────────────────────────

function NewTaskDialog({ open, onClose, onSubmit, taskTitles, users, orders, selectedOrder }: {
  open: boolean; onClose: () => void
  onSubmit: (data: Record<string, string>) => void
  taskTitles: string[]; users: { id: number; name: string }[]
  orders: { id: number; po_number: string }[]
  selectedOrder?: string
}) {
  const today = new Date().toISOString().slice(0, 10)
  const [form, setForm] = useState({
    title: '', assignee_id: '', customer_order_id: selectedOrder || '',
    start_date: today, deadline_date: '', description: '',
  })
  const [errors, setErrors] = useState<string[]>([])
  const [submitting, setSubmitting] = useState(false)

  const set = (key: string, val: string) => setForm(prev => ({ ...prev, [key]: val }))

  const titleOptions = taskTitles.map(t => ({ value: t, label: t }))
  const orderOptions = [{ value: '', label: 'None' }, ...orders.map(o => ({ value: String(o.id), label: `#${o.id} ${o.po_number || ''}`.trim() }))]

  const handleSubmit = async () => {
    const errs: string[] = []
    if (!form.title) errs.push('Title is required')
    if (!form.deadline_date) errs.push('Deadline is required')
    if (!form.assignee_id) errs.push('Assignee is required')
    if (errs.length > 0) { setErrors(errs); return }
    setErrors([])
    setSubmitting(true)
    await onSubmit(form)
    setSubmitting(false)
  }

  const handleClose = () => {
    setForm({ title: '', assignee_id: '', customer_order_id: selectedOrder || '', start_date: today, deadline_date: '', description: '' })
    setErrors([])
    onClose()
  }

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>New Task</DialogTitle>
      <DialogBody>
        {errors.length > 0 && (
          <div className="mb-3 rounded-lg border border-red-200 bg-red-50 p-2 dark:border-red-800 dark:bg-red-900/20">
            <ul className="list-disc pl-4 text-xs text-red-700 dark:text-red-400">
              {errors.map((e, i) => <li key={i}>{e}</li>)}
            </ul>
          </div>
        )}
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-zinc-700 dark:text-zinc-300">Title *</label>
            <FormMultiSelect
              values={form.title ? [form.title] : []}
              options={titleOptions}
              onChange={v => set('title', String(v[v.length - 1] || ''))}
              allowCreate
              placeholder="Select or type title..."
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-zinc-700 dark:text-zinc-300">Assignee *</label>
            <AssigneeSelect
              value={form.assignee_id}
              users={users}
              onChange={v => set('assignee_id', v)}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-zinc-700 dark:text-zinc-300">Order</label>
            <FormMultiSelect
              values={form.customer_order_id ? [form.customer_order_id] : []}
              options={orderOptions.filter(o => o.value !== '')}
              onChange={v => set('customer_order_id', String(v[v.length - 1] || ''))}
              placeholder="Search order..."
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-zinc-700 dark:text-zinc-300">Start Date</label>
              <FormInput type="date" value={form.start_date} onChange={e => set('start_date', (e.target as HTMLInputElement).value)} />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-zinc-700 dark:text-zinc-300">Deadline *</label>
              <FormInput type="date" value={form.deadline_date} onChange={e => set('deadline_date', (e.target as HTMLInputElement).value)} />
            </div>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-zinc-700 dark:text-zinc-300">Description</label>
            <FormTextarea value={form.description} onChange={e => set('description', (e.target as HTMLTextAreaElement).value)} rows={3} placeholder="Task details..." />
          </div>
        </div>
      </DialogBody>
      <DialogActions>
        <Button variant="ghost" onClick={handleClose}>Cancel</Button>
        <Button disabled={submitting} onClick={handleSubmit}>
          {submitting ? 'Creating...' : 'Create Task'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// ─── Task Calendar View ─────────────────────────

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

function TaskCalendar({ tasks, month, onMonthChange, scale, onScaleChange, weekStart, onWeekStartChange, onDayClick, onNewTask }: {
  tasks: TaskRow[]; month: { year: number; month: number }
  onMonthChange: (m: { year: number; month: number }) => void
  scale: 'month' | 'week' | 'year'; onScaleChange: (s: 'month' | 'week' | 'year') => void
  weekStart: string; onWeekStartChange: (ws: string) => void
  currentUserId: number; onComplete: (t: TaskRow) => void; onDelete: (id: number) => void; onReminder: (id: number) => void
  onDayClick: (dateLabel: string, tasks: TaskRow[]) => void
  onNewTask: () => void
}) {
  const todayStr = new Date().toISOString().slice(0, 10)

  // Tasks by date: completed → completion_date, uncompleted → today
  const tasksByDate: Map<string, TaskRow[]> = useMemo(() => {
    const map = new Map<string, TaskRow[]>()
    tasks.forEach(t => {
      const key = t.completed && t.completion_date ? t.completion_date : (!t.completed ? todayStr : null)
      if (!key) return
      const arr = map.get(key) || []; arr.push(t); map.set(key, arr)
    })
    return map
  }, [tasks, todayStr])

  const toLocalDateStr = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

  const prev = () => {
    if (scale === 'year') onMonthChange({ ...month, year: month.year - 1 })
    else if (scale === 'week') {
      const parts = weekStart.split('-').map(Number)
      const d = new Date(parts[0], parts[1] - 1, parts[2])
      d.setDate(d.getDate() - 7)
      onWeekStartChange(toLocalDateStr(d))
    } else onMonthChange({ year: month.month === 0 ? month.year - 1 : month.year, month: month.month === 0 ? 11 : month.month - 1 })
  }
  const next = () => {
    if (scale === 'year') onMonthChange({ ...month, year: month.year + 1 })
    else if (scale === 'week') {
      const parts = weekStart.split('-').map(Number)
      const d = new Date(parts[0], parts[1] - 1, parts[2])
      d.setDate(d.getDate() + 7)
      onWeekStartChange(toLocalDateStr(d))
    } else onMonthChange({ year: month.month === 11 ? month.year + 1 : month.year, month: month.month === 11 ? 0 : month.month + 1 })
  }
  const goToday = () => {
    const d = new Date()
    onMonthChange({ year: d.getFullYear(), month: d.getMonth() })
    // Also reset weekStart to current week
    const ws = new Date()
    ws.setDate(ws.getDate() - ws.getDay())
    onWeekStartChange(toLocalDateStr(ws))
  }

  const now = new Date()
  const currentWeekStart = (() => { const d = new Date(); d.setDate(d.getDate() - d.getDay()); return toLocalDateStr(d) })()
  const isOnToday = scale === 'year'
    ? month.year === now.getFullYear()
    : scale === 'week'
      ? weekStart === currentWeekStart
      : month.year === now.getFullYear() && month.month === now.getMonth()

  const scaleLabels: Record<string, string> = { month: 'Month', week: 'Week', year: 'Year' }

  const makeDate = (y: number, m: number, d: number) => `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`

  // ─── Header title ─────────────────────────
  const headerTitle = (() => {
    if (scale === 'year') return String(month.year)
    if (scale === 'week') {
      const parts = weekStart.split('-').map(Number)
      const start = new Date(parts[0], parts[1] - 1, parts[2])
      const end = new Date(start); end.setDate(start.getDate() + 6)
      const startMonth = MONTHS[start.getMonth()].slice(0, 3)
      const endMonth = MONTHS[end.getMonth()].slice(0, 3)
      return `${startMonth} ${start.getDate()} - ${endMonth} ${end.getDate()}, ${end.getFullYear()}`
    }
    return `${MONTHS[month.month]} ${month.year}`
  })()
  const headerDateTime = scale === 'year'
    ? String(month.year)
    : scale === 'week'
      ? weekStart
      : `${month.year}-${String(month.month + 1).padStart(2, '0')}`

  // ─── Month grid (Tailwind UI style) ───────
  const renderMonthGrid = (y: number, m: number) => {
    const first = new Date(y, m, 1)
    const last = new Date(y, m + 1, 0)
    const pad = first.getDay()
    const days = last.getDate()
    // Also fill previous month days
    const prevLast = new Date(y, m, 0)
    const prevDays = prevLast.getDate()
    const cells: { day: number; currentMonth: boolean; dateStr: string }[] = []
    for (let i = pad - 1; i >= 0; i--) {
      const d = prevDays - i
      const pm = m === 0 ? 11 : m - 1
      const py = m === 0 ? y - 1 : y
      cells.push({ day: d, currentMonth: false, dateStr: makeDate(py, pm, d) })
    }
    for (let d = 1; d <= days; d++) cells.push({ day: d, currentMonth: true, dateStr: makeDate(y, m, d) })
    while (cells.length % 7 !== 0) {
      const d = cells.length - pad - days + 1
      const nm = m === 11 ? 0 : m + 1
      const ny = m === 11 ? y + 1 : y
      cells.push({ day: d, currentMonth: false, dateStr: makeDate(ny, nm, d) })
    }

    return (
      <div className="shadow-sm ring-1 ring-black/5 dark:ring-white/10 lg:flex lg:flex-auto lg:flex-col">
        {/* Day names header */}
        <div className="grid grid-cols-7 gap-px border-b border-gray-300 bg-gray-200 text-center text-xs/6 font-semibold text-gray-700 lg:flex-none dark:border-zinc-600 dark:bg-zinc-700 dark:text-zinc-400">
          {DAYS.map(d => (
            <div key={d} className="flex justify-center bg-white py-2 dark:bg-zinc-900">
              <span>{d.charAt(0)}</span>
              <span className="sr-only sm:not-sr-only">{d.slice(1)}</span>
            </div>
          ))}
        </div>
        {/* Day cells */}
        <div className="flex flex-auto bg-gray-200 text-xs/6 dark:bg-zinc-700 lg:flex-auto">
          <div className="grid w-full grid-cols-7 gap-px">
            {cells.map((cell, i) => {
              const isToday = cell.dateStr === todayStr
              const dayTasks = tasksByDate.get(cell.dateStr) || []
              return (
                <div
                  key={`${cell.dateStr}-${i}`}
                  className={clsx(
                    'min-h-24 cursor-pointer px-3 py-2 transition-colors',
                    cell.currentMonth
                      ? 'bg-white hover:bg-gray-50 dark:bg-zinc-900 dark:hover:bg-zinc-800'
                      : 'bg-gray-50 text-gray-500 hover:bg-gray-100 dark:bg-zinc-800/50 dark:text-zinc-500 dark:hover:bg-zinc-800',
                  )}
                  onClick={() => dayTasks.length > 0 && onDayClick(formatDate(cell.dateStr), dayTasks)}
                >
                  <time
                    dateTime={cell.dateStr}
                    className={clsx(
                      'flex size-6 items-center justify-center',
                      isToday && 'rounded-full bg-indigo-600 font-semibold text-white',
                      !isToday && cell.currentMonth && 'text-gray-900 dark:text-white',
                    )}
                  >
                    {cell.day}
                  </time>
                  {/* Task dots on mobile, names on desktop */}
                  {dayTasks.length > 0 && (
                    <>
                      {/* Mobile: dots */}
                      <div className="mt-1 flex flex-wrap gap-0.5 lg:hidden">
                        {dayTasks.slice(0, 3).map(t => (
                          <span
                            key={t.id}
                            className={clsx(
                              'inline-block size-1.5 rounded-full',
                              t.completed ? 'bg-emerald-500' : isOverdue(t.deadline_date, false) ? 'bg-red-500' : 'bg-amber-500',
                            )}
                          />
                        ))}
                        {dayTasks.length > 3 && <span className="text-[9px] text-gray-400">+{dayTasks.length - 3}</span>}
                      </div>
                      {/* Desktop: task names */}
                      <ol className="mt-2 hidden lg:block">
                        {dayTasks.slice(0, 2).map(t => (
                          <li key={t.id}>
                            <div className="group flex items-center gap-1">
                              <span className={clsx('inline-block size-1.5 shrink-0 rounded-full', t.completed ? 'bg-emerald-500' : isOverdue(t.deadline_date, false) ? 'bg-red-500' : 'bg-amber-500')} />
                              <p className={clsx('flex-auto truncate font-medium text-gray-900 group-hover:text-indigo-600 dark:text-white dark:group-hover:text-indigo-400', t.completed && 'line-through !text-gray-400 dark:!text-zinc-500')}>{t.title}</p>
                            </div>
                          </li>
                        ))}
                        {dayTasks.length > 2 && <li className="text-gray-500 dark:text-zinc-500">+ {dayTasks.length - 2} more</li>}
                      </ol>
                    </>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      </div>
    )
  }

  // ─── Week view ────────────────────────────
  const renderWeek = () => {
    const parts = weekStart.split('-').map(Number)
    const start = new Date(parts[0], parts[1] - 1, parts[2])
    const weekDays = Array.from({ length: 7 }, (_, i) => {
      const d = new Date(start); d.setDate(start.getDate() + i)
      return { date: d, dateStr: toLocalDateStr(d), dayName: DAYS[d.getDay()], dayNum: d.getDate(), monthShort: MONTHS[d.getMonth()].slice(0, 3) }
    })
    return (
      <div className="rounded-lg shadow-sm ring-1 ring-gray-200 dark:ring-zinc-700">
        {/* Sticky day header row */}
        <div className="grid grid-cols-7 gap-px border-b border-gray-300 bg-gray-200 dark:border-zinc-600 dark:bg-zinc-700">
          {weekDays.map(wd => {
            const isToday = wd.dateStr === todayStr
            return (
              <div key={wd.dateStr} className="flex flex-col items-center bg-white py-2 dark:bg-zinc-900">
                <span className={clsx('text-xs font-semibold', isToday ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-700 dark:text-zinc-400')}>{wd.dayName}</span>
                <span className={clsx(
                  'mt-0.5 flex size-7 items-center justify-center text-sm font-semibold',
                  isToday && 'rounded-full bg-indigo-600 text-white',
                  !isToday && 'text-gray-900 dark:text-white',
                )}>
                  {wd.dayNum}
                </span>
                <span className="text-[10px] text-gray-500 dark:text-zinc-500">{wd.monthShort}</span>
              </div>
            )
          })}
        </div>
        {/* Task columns */}
        <div className="grid grid-cols-7 gap-px bg-gray-200 dark:bg-zinc-700" style={{ minHeight: 'calc(100vh - 280px)' }}>
          {weekDays.map(wd => {
            const dayTasks = tasksByDate.get(wd.dateStr) || []
            const isToday = wd.dateStr === todayStr
            return (
              <div key={wd.dateStr} className={clsx('flex flex-col overflow-y-auto bg-white dark:bg-zinc-900', isToday && 'bg-indigo-50/30 dark:bg-indigo-500/5')}>
                <div className="flex-1 space-y-1.5 p-1.5">
                  {dayTasks.map(t => (
                    <div
                      key={t.id}
                      className={clsx(
                        'cursor-pointer rounded-md border-l-[3px] bg-white p-2 text-[11px] shadow-xs ring-1 ring-gray-200 transition-colors hover:ring-gray-300 dark:bg-zinc-800 dark:ring-zinc-700 dark:hover:ring-zinc-600',
                        t.completed ? 'border-l-emerald-500' : isOverdue(t.deadline_date, false) ? 'border-l-red-500' : 'border-l-amber-500',
                      )}
                      onClick={() => onDayClick(t.title, [t])}
                    >
                      <div className={clsx('truncate font-medium text-gray-900 dark:text-white', t.completed && 'line-through text-gray-400 dark:text-zinc-500')}>{t.title}</div>
                      <div className="mt-0.5 truncate text-[10px] text-gray-500 dark:text-zinc-400">{t.assignee_name}</div>
                    </div>
                  ))}
                  {dayTasks.length === 0 && <div className="py-8 text-center text-[10px] text-gray-300 dark:text-zinc-600">No tasks</div>}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  // ─── Year view (mini calendars) ───────────
  const renderYearGrid = () => {
    return (
      <div className="grid grid-cols-1 gap-x-8 gap-y-16 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
        {Array.from({ length: 12 }, (_, m) => {
          const first = new Date(month.year, m, 1)
          const last = new Date(month.year, m + 1, 0)
          const pad = first.getDay()
          const days = last.getDate()
          const cells: (number | null)[] = []
          for (let i = 0; i < pad; i++) cells.push(null)
          for (let d = 1; d <= days; d++) cells.push(d)
          while (cells.length % 7 !== 0) cells.push(null)

          return (
            <div key={m}>
              <button
                onClick={() => { onMonthChange({ year: month.year, month: m }); onScaleChange('month') }}
                className="text-sm font-semibold text-gray-900 hover:text-indigo-600 dark:text-white dark:hover:text-indigo-400"
              >
                {MONTHS[m]}
              </button>
              <div className="mt-6 grid grid-cols-7 text-center text-xs/6 text-gray-500 dark:text-zinc-500">
                {DAYS.map(d => <div key={d}>{d.charAt(0)}</div>)}
              </div>
              <div className="mt-2 grid grid-cols-7 gap-px rounded-lg bg-gray-200 text-center text-sm shadow-sm ring-1 ring-gray-200 dark:bg-zinc-700 dark:ring-zinc-700">
                {cells.map((day, i) => {
                  if (day === null) {
                    return (
                      <div
                        key={`e${i}`}
                        className={clsx(
                          'bg-gray-50 py-1.5 dark:bg-zinc-800/50',
                          i === 0 && 'rounded-tl-lg',
                          i === 6 && 'rounded-tr-lg',
                          i === cells.length - 7 && 'rounded-bl-lg',
                          i === cells.length - 1 && 'rounded-br-lg',
                        )}
                      />
                    )
                  }
                  const dateStr = makeDate(month.year, m, day)
                  const isToday = dateStr === todayStr
                  const hasTasks = tasksByDate.has(dateStr)
                  return (
                    <div
                      key={dateStr}
                      className={clsx(
                        'relative cursor-pointer py-1.5 transition-colors hover:bg-gray-100 dark:hover:bg-zinc-800',
                        'bg-white dark:bg-zinc-900',
                        i === 0 && 'rounded-tl-lg',
                        i === 6 && 'rounded-tr-lg',
                        i === cells.length - 7 && 'rounded-bl-lg',
                        i === cells.length - 1 && 'rounded-br-lg',
                      )}
                      onClick={() => {
                        const dt = tasksByDate.get(dateStr)
                        if (dt && dt.length > 0) onDayClick(formatDate(dateStr), dt)
                      }}
                    >
                      <time
                        dateTime={dateStr}
                        className={clsx(
                          'mx-auto flex size-7 items-center justify-center',
                          isToday && 'rounded-full bg-indigo-600 font-semibold text-white',
                          !isToday && 'text-gray-900 dark:text-zinc-300',
                        )}
                      >
                        {day}
                      </time>
                      {hasTasks && (() => {
                        const count = tasksByDate.get(dateStr)!.length
                        return (
                          <span className="absolute bottom-0 left-1/2 -translate-x-1/2 flex items-center gap-px">
                            <span className={clsx('inline-block size-1.5 rounded-full', isToday ? 'bg-white' : 'bg-indigo-500')} />
                            {count > 1 && <span className={clsx('text-[7px] font-bold leading-none', isToday ? 'text-white' : 'text-indigo-500')}>{count}</span>}
                          </span>
                        )
                      })()}
                    </div>
                  )
                })}
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <div data-testid="calendar-view">
      {/* Tailwind UI Calendar Header */}
      <header className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-zinc-700 lg:flex-none">
        <h1 className="text-base font-semibold text-gray-900 dark:text-white">
          <time dateTime={headerDateTime}>{headerTitle}</time>
        </h1>
        <div className="flex items-center">
          {/* Prev / Today / Next button group */}
          <div className="relative flex items-center rounded-md bg-white shadow-xs outline -outline-offset-1 outline-gray-300 dark:bg-zinc-800 dark:outline-zinc-600 md:items-stretch">
            <button
              onClick={prev}
              className="flex h-9 w-12 items-center justify-center rounded-l-md pr-1 text-gray-400 hover:text-gray-500 focus:relative md:w-9 md:pr-0 md:hover:bg-gray-50 dark:text-zinc-400 dark:hover:text-zinc-300 dark:md:hover:bg-zinc-700"
              title="Previous"
            >
              <ChevronLeftIcon aria-hidden="true" className="size-5" />
            </button>
            {!isOnToday && (
              <button
                onClick={goToday}
                className="hidden border-x border-gray-300 px-3.5 text-sm font-semibold text-gray-900 hover:bg-gray-50 focus:relative dark:border-zinc-600 dark:text-white dark:hover:bg-zinc-700 md:block"
                data-testid="calendar-today"
              >
                Today
              </button>
            )}
            <span className="relative -mx-px h-5 w-px bg-gray-300 dark:bg-zinc-600 md:hidden" />
            <button
              onClick={next}
              className="flex h-9 w-12 items-center justify-center rounded-r-md pl-1 text-gray-400 hover:text-gray-500 focus:relative md:w-9 md:pl-0 md:hover:bg-gray-50 dark:text-zinc-400 dark:hover:text-zinc-300 dark:md:hover:bg-zinc-700"
              title="Next"
            >
              <ChevronRightIcon className="size-5" />
            </button>
          </div>
          {/* View dropdown + New Task */}
          <div className="hidden md:ml-4 md:flex md:items-center">
            <Menu as="div" className="relative">
              <MenuButton className="flex items-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs inset-ring inset-ring-gray-300 hover:bg-gray-50 dark:bg-zinc-800 dark:text-white dark:inset-ring-zinc-600 dark:hover:bg-zinc-700">
                {scaleLabels[scale]}
                <ChevronDownIcon className="-mr-1 size-5 text-gray-400" />
              </MenuButton>
              <MenuItems className="absolute right-0 z-10 mt-3 w-36 origin-top-right overflow-hidden rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-none dark:bg-zinc-800 dark:ring-white/10">
                <div className="py-1">
                  {(['month', 'week', 'year'] as const).map(s => (
                    <MenuItem key={s}>
                      <button
                        onClick={() => onScaleChange(s)}
                        className={clsx(
                          'block w-full px-4 py-2 text-left text-sm data-[focus]:bg-gray-100 dark:data-[focus]:bg-zinc-700',
                          scale === s ? 'font-semibold text-indigo-600 dark:text-indigo-400' : 'text-gray-700 dark:text-zinc-300',
                        )}
                      >
                        {scaleLabels[s]} view
                      </button>
                    </MenuItem>
                  ))}
                </div>
              </MenuItems>
            </Menu>
            <div className="ml-6 h-6 w-px bg-gray-300 dark:bg-zinc-600" />
            <button
              type="button"
              onClick={onNewTask}
              className="ml-6 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            >
              New Task
            </button>
          </div>
        </div>
      </header>

      {/* Calendar body */}
      <div>
        {scale === 'month' && renderMonthGrid(month.year, month.month)}
        {scale === 'week' && renderWeek()}
        {scale === 'year' && renderYearGrid()}
      </div>
    </div>
  )
}

// ─── Task Drawer (shared Drawer component) ──────

function TaskDrawer({ open, dateLabel, tasks, currentUserId, onComplete, onDelete, onReminder, onClose }: {
  open: boolean; dateLabel: string; tasks: TaskRow[]
  currentUserId: number; onComplete: (t: TaskRow) => void
  onDelete: (id: number) => void; onReminder: (id: number) => void; onClose: () => void
}) {
  const nameOrMe = (id: number | null, name: string | null) => id === currentUserId ? 'Me' : (name || '—')
  const title = tasks.length === 1 ? tasks[0]?.title || dateLabel : `Tasks for ${dateLabel}`

  return (
    <Drawer open={open} onClose={onClose} title={title}>
      <div data-testid="task-drawer">
        {tasks.length === 0 && <p className="text-sm text-zinc-400">No tasks</p>}
        <div className="space-y-3">
          {tasks.map(t => (
            <div key={t.id} className={tasks.length > 1 ? clsx('rounded-xl border p-3', t.completed ? 'border-emerald-200 bg-emerald-50/50 dark:border-emerald-800 dark:bg-emerald-900/10' : isOverdue(t.deadline_date, false) ? 'border-red-200 bg-red-50/50 dark:border-red-800 dark:bg-red-900/10' : 'border-zinc-200 bg-white dark:border-zinc-700 dark:bg-zinc-800') : ''}>
              {tasks.length > 1 && (
                <div className="flex items-start gap-2">
                  <span className={clsx('mt-1 inline-block h-2 w-2 shrink-0 rounded-full', t.completed ? 'bg-emerald-500' : isOverdue(t.deadline_date, false) ? 'bg-red-500' : 'bg-amber-500')} />
                  <div className="flex-1">
                    <div className={clsx('text-sm font-semibold text-zinc-900 dark:text-white', t.completed && 'line-through opacity-50')}>{t.title}</div>
                    <div className="mt-0.5 text-xs text-zinc-500 dark:text-zinc-400">
                      {nameOrMe(t.user_id, t.user_name)} → {nameOrMe(t.assignee_id, t.assignee_name)}
                    </div>
                  </div>
                </div>
              )}
              {tasks.length === 1 && (
                <div className="text-xs text-zinc-500 dark:text-zinc-400">
                  {nameOrMe(t.user_id, t.user_name)} → {nameOrMe(t.assignee_id, t.assignee_name)}
                </div>
              )}

              <div className="mt-2 grid grid-cols-2 gap-1 text-[11px] text-zinc-600 dark:text-zinc-400">
                <div>Start: <strong>{formatDate(t.start_date)}</strong></div>
                <div>Deadline: <strong>{formatDate(t.deadline_date)}</strong></div>
                {t.order_id && <div className="col-span-2">Order: <Link href={`/inertia_customer_orders/${t.order_id}`} className="text-indigo-600 hover:underline dark:text-indigo-400">#{t.order_id} {t.order_po_number}</Link></div>}
                {t.completed && t.completion_date && <div className="col-span-2">Completed: <strong>{formatDate(t.completion_date)}</strong></div>}
              </div>

              {t.description && (
                <div className="mt-2 rounded-lg bg-zinc-50 p-2 text-xs text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400" dangerouslySetInnerHTML={{ __html: t.description }} />
              )}

              <div className="mt-2 flex items-center gap-1 border-t border-zinc-100 pt-2 dark:border-zinc-700">
                {t.user_id !== currentUserId && (
                  <button onClick={() => onComplete(t)} className={clsx('inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs', t.completed ? 'text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-500/10' : 'text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-700')}>
                    <CheckCircleIcon className="h-3.5 w-3.5" /> {t.completed ? 'Reopen' : 'Complete'}
                  </button>
                )}
                <button onClick={() => onReminder(t.id)} className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-700">
                  <EnvelopeIcon className="h-3.5 w-3.5" /> Remind
                </button>
                <button onClick={() => { if (confirm('Delete this task?')) onDelete(t.id) }} className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10">
                  <TrashIcon className="h-3.5 w-3.5" /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Drawer>
  )
}

// ─── Assignee Select with Avatars ───────────────

function AssigneeSelect({ value, users, onChange }: { value: string; users: { id: number; name: string }[]; onChange: (v: string) => void }) {
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const filtered = query ? users.filter(u => u.name.toLowerCase().includes(query.toLowerCase())) : users
  const selected = users.find(u => String(u.id) === value)

  const select = (u: { id: number }) => {
    onChange(String(u.id))
    setQuery('')
    setOpen(false)
  }

  return (
    <div className="relative">
      {selected ? (
        <div className="flex items-center gap-2 rounded-lg border border-zinc-950/10 bg-transparent px-2 py-[calc(--spacing(1.5)-1px)] dark:border-white/10 dark:bg-white/5">
          <Avatar name={selected.name} size="sm" />
          <span className="flex-1 text-sm text-zinc-900 dark:text-white">{selected.name}</span>
          <button type="button" onClick={() => { onChange(''); setTimeout(() => inputRef.current?.focus(), 50) }} className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300">
            <XMarkIcon className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : (
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true) }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder="Search assignee..."
          className="w-full rounded-lg border border-zinc-950/10 bg-transparent px-2 py-[calc(--spacing(1.5)-1px)] text-sm text-zinc-900 placeholder:text-zinc-500 focus:outline-none dark:border-white/10 dark:bg-white/5 dark:text-white"
        />
      )}
      {open && !selected && filtered.length > 0 && (
        <div className="absolute left-0 z-50 mt-1 max-h-48 w-full overflow-y-auto rounded-xl bg-white/75 p-1 shadow-lg ring-1 ring-zinc-950/10 backdrop-blur-xl dark:bg-zinc-800/75 dark:ring-white/10">
          {filtered.map(u => (
            <button
              key={u.id}
              type="button"
              onMouseDown={e => e.preventDefault()}
              onClick={() => select(u)}
              className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm hover:bg-indigo-500 hover:text-white dark:text-white"
            >
              <Avatar name={u.name} size="sm" />
              {u.name}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

TasksIndex.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
